import type { TFunction } from 'i18next'
import { uniq } from 'lodash-es'
import type { GenerationModelType } from 'src/services/apollo/generated/graphql'
import { isMMDIT26AModel } from '~/utils/model'

type TrainLoraInput = {
  baseModelId: string
  mediaIds: string[]
  title: string | undefined
  /**
   * Inherited from submission parameters
   * Hardcoded to GenerationModelType.UserMultiLora
   * Not useful for validation
   */
  type: GenerationModelType
  triggerWords: string | undefined
  primaryLoraModelId?: string
  trainingTaskId?: string
  category?: string
  kaisuukenId?: string
}

type ExtraValidateInfo = {
  // Validate the actual modelType
  modelType?: GenerationModelType
}

export const MAX_ALLOWED_TRIGGER_WORDS_LENGTH = 256
export const MIN_TRIGGER_WORDS_LENGTH_MMDIT26A = 30

export function validateTrainLoraInput(
  t: TFunction<['market', 'trainlora']>,
  input: TrainLoraInput,
  extraValidateInfo: ExtraValidateInfo,
) {
  if (!input.baseModelId)
    throw new Error(t('market:train-lora.notice.model-needed'))
  if (input.mediaIds.length < 10 && !input.trainingTaskId)
    throw new Error(t('market:train-lora.notice.too-few-images'))
  if (!input.title || input.title.length < 1)
    throw new Error(t('market:train-lora.notice.title-invalid'))
  const triggerWords = input.triggerWords?.replace(/[\r\n]+/g, ', ')
  if (!triggerWords || triggerWords.length < 1)
    throw new Error(t('market:train-lora.notice.trigger-words-invalid'))
  if (triggerWords.length > MAX_ALLOWED_TRIGGER_WORDS_LENGTH)
    throw new Error(t('market:train-lora.notice.trigger-words-too-long'))

  if (
    isMMDIT26AModel(extraValidateInfo.modelType) &&
    triggerWords.length < MIN_TRIGGER_WORDS_LENGTH_MMDIT26A
  )
    throw new Error(
      t('trainlora:submit-form.notice.trigger-words-too-short-mmdit26a'),
    )

  if (!input.category)
    throw new Error(
      t('trainlora:submit-form.notice.category-needed', {
        defaultValue: 'Category is required',
      }),
    )

  return { ...input, triggerWords }
}

export function getTrainLoraInputTrackAttrs(
  input: TrainLoraInput,
  trackingArgs?: {
    modelType?: GenerationModelType
    modelTheme?: string
    trainedLoraList?: string[]
    credit?: number
    mediaSourceMap?: Map<string, 'task' | 'upload' | 'dataset'>
  },
) {
  const { trainedLoraList, mediaSourceMap, ...rest } = trackingArgs ?? {}

  let taskImagesCount = 0
  let uploadImagesCount = 0
  let datasetImagesCount = 0
  mediaSourceMap?.forEach(v => {
    if (v === 'task') taskImagesCount += 1
    if (v === 'upload') uploadImagesCount += 1
    if (v === 'dataset') datasetImagesCount += 1
  })

  return {
    trainModel: input.baseModelId,
    trainInfo: input.title,
    triggerWords: input.triggerWords,
    imagesCount: input.mediaIds.length,
    category: input.category,
    trainedLoraList: trainedLoraList ? uniq(trainedLoraList) : [],
    trainedLoraCount: trainedLoraList?.length,
    taskImagesCount,
    uploadImagesCount,
    datasetImagesCount,
    ...rest,
  }
}
